// Copyright TraderEvolution Global LTD. © 2017-2026. All rights reserved.
// main question what we need to do if chart have aggregated history?
var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import { CustomEvent } from '@shared/utils/CustomEvents';
import { AutoLoadingState } from './AutoLoadingState';
import { ChartLoadTrigger } from './TerceraChartMVC';
import { HistoryManager } from '@shared/commons/cache/History/HistoryManager';
import { DataCache } from '@shared/commons/DataCache';
export class AutoLoadHistoryItem {
    constructor(side) {
        this.barsForNextLoading = 0;
        this.isLoadingHistoryNow = false;
        this.side = side;
    }
}
// how to append new data to chart?
export class TerceraChartAutoLoadHistoryManager {
    constructor(chart) {
        this.loadingStateChanged = new CustomEvent();
        this.chart = chart;
        this.allowAutoLoad = false;
        this.leftItem = new AutoLoadHistoryItem(ChartLoadTrigger.ScrollingLeft);
        this.rightItem = new AutoLoadHistoryItem(ChartLoadTrigger.ScrollingRight);
    }
    // chart was changed by zooming or resizing
    resetCurrentView() {
        // need reload history
    }
    dispose() {
        // need stop load history
        this.stopLoading();
    }
    stopLoading() {
        this.leftItem.isLoadingHistoryNow = false;
        this.leftItem.barsForNextLoading = 0;
        this.rightItem.isLoadingHistoryNow = false;
        this.rightItem.barsForNextLoading = 0;
    }
    setBarsToLoad(needToLoadBars) {
        if (this.checkChartState()) {
            this.setLeftPaddingBarsCount(needToLoadBars);
        }
    }
    processMouseUp() {
        if (!this.allowAutoLoad) {
            return false;
        }
        void this.update();
        return true;
        // if loading stop loading
        // load history
        // how many bars need to load?
        // insert empty bars into CashItemSeries
    }
    update() {
        return __awaiter(this, void 0, void 0, function* () {
            // check if need to addiditionaly load history
            // for example view was changed by zooming or resizing
            void this.loadHistory(this.leftItem);
            void this.loadHistory(this.rightItem);
        });
    }
    processDragRight(newI1) {
        if (!this.allowAutoLoad) {
            return;
        }
        if (!this.checkChartState()) {
            return;
        }
        const chart = this.chart;
        const cashItemSeries = chart.MainCashItemSeries;
        const barsCount = chart.BarsCount() - cashItemSeries.leftPaddingBarsCount;
        const lastRightPaddingBarsCount = cashItemSeries.rightPaddingBarsCount;
        const possibleLoadBarsCount = Math.ceil(newI1 + lastRightPaddingBarsCount - barsCount + 1);
        const needToLoadBars = Math.max(possibleLoadBarsCount, 0);
        this.rightItem.barsForNextLoading = needToLoadBars;
        this.setRightPaddingBarsCount(needToLoadBars);
    }
    processDragLeft(newI1) {
        if (!this.allowAutoLoad) {
            return;
        }
        if (!this.checkChartState()) {
            return;
        }
        const chart = this.chart;
        const possibleLoadBarsCount = Math.floor(chart.mainWindow.im() - newI1);
        const needToLoadBars = Math.max(possibleLoadBarsCount, 0);
        this.leftItem.barsForNextLoading = needToLoadBars;
        this.setLeftPaddingBarsCount(needToLoadBars);
    }
    loadHistory(item) {
        return __awaiter(this, void 0, void 0, function* () {
            const cashItemSeries = this.chart.MainCashItemSeries;
            const cashItem = cashItemSeries === null || cashItemSeries === void 0 ? void 0 : cashItemSeries.CashItem;
            const isScrollingLeft = item.side === ChartLoadTrigger.ScrollingLeft;
            if (isNullOrUndefined(cashItemSeries) ||
                (isScrollingLeft && cashItem.isMaxHistoryLoaded) ||
                (!isScrollingLeft && cashItem.isRealTimeBuilding)) {
                return;
            }
            const chart = this.chart;
            const historyParams = chart.GetHistoryParams(null, null, item.side);
            if (historyParams.count <= 0 || item.isLoadingHistoryNow) {
                return;
            }
            this.loadingStateChanged.Raise(AutoLoadingState.LoadinStarted);
            item.isLoadingHistoryNow = true;
            const cashItemFromChart = cashItemSeries.CashItem;
            const paddingIntervals = yield HistoryManager.getHistory(historyParams, null);
            if (!item.isLoadingHistoryNow) {
                return;
            }
            cashItemFromChart.InsertPaddingHistory(paddingIntervals, historyParams);
            chart.LoadHistoryFinish(cashItemFromChart, true, item.side, historyParams);
            const downloadedBars = cashItemFromChart.lastLoadedBarsCount;
            this.returnPaddingBars(cashItemSeries, item);
            this.updatePaddingBars(item, downloadedBars);
            if (isScrollingLeft) {
                chart.updateRightIndexByDownloadedBars(downloadedBars);
            }
            yield this.loadOverlays(item, historyParams);
            item.isLoadingHistoryNow = false;
            this.loadingStateChanged.Raise(AutoLoadingState.LoadinFinised);
            void this.loadHistory(item);
        });
    }
    updatePaddingBars(item, loadedBars) {
        const additionalBarsForLoading = item.barsForNextLoading - loadedBars;
        item.barsForNextLoading = 0;
        if (additionalBarsForLoading < 1) {
            return;
        }
        if (item.side === ChartLoadTrigger.ScrollingLeft) {
            this.setLeftPaddingBarsCount(additionalBarsForLoading);
        }
        else {
            this.setRightPaddingBarsCount(additionalBarsForLoading);
        }
    }
    returnPaddingBars(oldSeries, item) {
        if (item.side === ChartLoadTrigger.ScrollingLeft) {
            if (!this.rightItem.isLoadingHistoryNow) {
                this.setRightPaddingBarsCount(oldSeries.rightPaddingBarsCount);
            }
        }
        else if (!this.leftItem.isLoadingHistoryNow) {
            this.setLeftPaddingBarsCount(oldSeries.leftPaddingBarsCount);
        }
    }
    checkChartState() {
        const chart = this.chart;
        const cashItemSeries = chart.MainCashItemSeries;
        if (isNullOrUndefined(cashItemSeries)) {
            return false;
        }
        return true;
    }
    setPaddingBarsCount(needToLoadBars, setPaddingMethod) {
        const chart = this.chart;
        const cashItemSeries = chart.MainCashItemSeries;
        setPaddingMethod(cashItemSeries, needToLoadBars);
        const len = chart.Overlays.length;
        for (let i = 0; i < len; i++) {
            const ser = chart.Overlays[i].mainPriceRenderer.Series;
            if (!isNullOrUndefined(ser)) {
                setPaddingMethod(ser, needToLoadBars);
            }
        }
    }
    setLeftPaddingBarsCount(needToLoadBars) {
        this.setPaddingBarsCount(needToLoadBars, (series, count) => {
            series.setLeftPaddingBarsCount(count);
        });
    }
    setRightPaddingBarsCount(needToLoadBars) {
        this.setPaddingBarsCount(needToLoadBars, (series, count) => {
            series.setRightPaddingBarsCount(count);
        });
    }
    loadOverlays(item, historyParams) {
        return __awaiter(this, void 0, void 0, function* () {
            const chart = this.chart;
            let tfInfo = chart.TimeFrameInfo();
            const account = chart.Account();
            const overlays = chart.Overlays;
            const isScrollingLeft = item.side === ChartLoadTrigger.ScrollingLeft;
            for (const overlay of overlays) {
                const spreadPlan = DataCache.GetSpreadPlan(account);
                tfInfo = tfInfo.Copy({ historyType: overlay.HistoryType, spreadPlan });
                const overlayHistoryParams = chart.GetHistoryParams(overlay.ActiveInstrument, tfInfo, ChartLoadTrigger.LoadingPeriodChanged);
                if (isScrollingLeft) {
                    overlayHistoryParams.ToTime = new Date(historyParams.ToTime.getTime());
                }
                else {
                    overlayHistoryParams.FromTime = new Date(historyParams.FromTime.getTime());
                    overlayHistoryParams.sortOrder = historyParams.sortOrder;
                }
                const cashItem = overlay.mainPriceRenderer.Series.CashItem;
                const paddingIntervals = yield HistoryManager.getHistory(overlayHistoryParams, null);
                cashItem.InsertPaddingHistory(paddingIntervals, overlayHistoryParams);
                chart.OnLoadOverlayCashItemFinish(overlay, cashItem, true);
            }
        });
    }
}
